<template>
  <ClientOverviewCard :title="$t('Relation summary')" :loading="loading">
    <template #tiles>
      <WdsStack justify-space-between gap-sm class="client-tiles">
        <div class="client-tile" data-test="client-relation-active">
          <WdsText large class="tw-font-large tw-text-gray-600">
            {{ $t('Active placements') }}
          </WdsText>
          <WdsStack align-center justify-space-between gap-sm>
            <WdsText large data-test="client-relation-active-count">
              {{ client?.activeHiresCount ?? 0 }}
            </WdsText>
            <WdsButtonGhost
              v-if="(client?.activeHiresCount ?? 0) > 0"
              secondary
              :href="activePlacementsUrl"
              data-test="client-relation-active-link"
            >
              {{ $t('View') }}
            </WdsButtonGhost>
          </WdsStack>
        </div>

        <div class="client-tile" data-test="client-relation-ending-soon">
          <WdsText large class="tw-font-large tw-text-gray-600">
            {{ $t('Ending within :days days', { days: `${SOON_DAYS}` }) }}
          </WdsText>
          <WdsStack align-center justify-space-between gap-sm>
            <WdsText large data-test="client-relation-ending-soon-count">
              {{ endingSoonCount }}
            </WdsText>
            <WdsButtonGhost
              v-if="endingSoonCount > 0"
              secondary
              :href="endingSoonUrl"
              data-test="client-relation-ending-soon-link"
            >
              {{ $t('View') }}
            </WdsButtonGhost>
          </WdsStack>
        </div>

        <div class="client-tile" data-test="client-relation-starting-soon">
          <WdsText large class="tw-font-large tw-text-gray-600">
            {{ $t('Starting within :days days', { days: `${SOON_DAYS}` }) }}
          </WdsText>
          <WdsStack align-center justify-space-between gap-sm>
            <WdsText large data-test="client-relation-starting-soon-count">
              {{ startingSoonCount }}
            </WdsText>
            <WdsButtonGhost
              v-if="startingSoonCount > 0"
              secondary
              :href="startingSoonUrl"
              data-test="client-relation-starting-soon-link"
            >
              {{ $t('View') }}
            </WdsButtonGhost>
          </WdsStack>
        </div>
      </WdsStack>
    </template>

    <WdsStack direction-column gap-md data-test="client-relation-card">
      <div v-if="client" class="tw-grid tw-grid-cols-3 tw-gap-4">
        <WdsText class="tw-font-large">
          <span class="tw-text-gray-900">{{ $t('Client added') }}</span>
        </WdsText>
        <WdsText class="tw-col-span-2" data-test="client-relation-since">{{ clientSince }}</WdsText>

        <WdsText class="tw-font-large">
          <span class="tw-text-gray-900">{{ $t('First placement') }}</span>
        </WdsText>
        <WdsText
          :secondary="!client.firstHireStartedAt"
          class="tw-col-span-2"
          data-test="client-relation-first-placement"
        >
          {{ firstPlacement ?? $t('No placements yet') }}
        </WdsText>

        <WdsText class="tw-font-large">
          <span class="tw-text-gray-900">{{ $t('Workers placed') }}</span>
        </WdsText>
        <div class="tw-col-span-2 tw-flex">
          <WdsButtonGhost
            secondary
            :href="placementsUrl"
            class="-tw-mx-1"
            data-test="client-relation-workers"
          >
            {{ $tc(':count worker | :count workers', client.workersCount) }}
          </WdsButtonGhost>
        </div>
      </div>
    </WdsStack>
  </ClientOverviewCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import moment from 'moment'
import { WdsButtonGhost, WdsStack, WdsText } from '@worksome/wds-components'
import { FragmentType, graphql, useFragment } from '@/gql'
import { HireActiveStatus } from '@/gql/graphql'
import { $t, $tc } from '@/app-bootstrapping/SetUpInternationalization'
import ClientOverviewCard from '@/components/Clients/OverviewCards/ClientOverviewCard.vue'
import {
  placementScope,
  clientPlacementsUrl,
  endingSoonPlacementsUrl,
  startingSoonPlacementsUrl,
} from '@/components/Clients/placementFilters'
import useDate from '@/composables/useDate'

const SOON_DAYS = 30

const { date: today } = useDate(new Date(), 'YYYY-MM-DD')
const { date: soonUntil } = useDate(moment().add(SOON_DAYS, 'days').toDate(), 'YYYY-MM-DD')
const soonRange = computed(() => ({ from: today.value, to: soonUntil.value }))

const ClientRelationCardFields = graphql(`
  fragment ClientRelationCard on ClientRelation {
    id
    createdAt
    activeHiresCount: hiresCount(status: [ACTIVE])
    workersCount
    firstHireStartedAt
    client {
      id
      name
    }
    ... on CompanySupplier {
      supplier {
        id
      }
    }
    ... on SupplierClient {
      managingCompany {
        id
        name
      }
    }
  }
`)

// Same query and arguments as the placements page the tiles link to, so count and list agree.
const ClientRelationUpcomingCountsQuery = graphql(`
  query ClientRelationUpcomingCounts(
    $supplierAccounts: [ID!]
    $companies: [ID!]
    $endClients: [ID!]
    $range: DateRangeInput
  ) {
    endingSoon: hires(
      supplierAccounts: $supplierAccounts
      companies: $companies
      endClients: $endClients
      endDateRange: $range
      first: 1
    ) {
      paginatorInfo {
        total
      }
    }
    startingSoon: hires(
      supplierAccounts: $supplierAccounts
      companies: $companies
      endClients: $endClients
      startDateRange: $range
      first: 1
    ) {
      paginatorInfo {
        total
      }
    }
  }
`)

const props = defineProps<{
  client: FragmentType<typeof ClientRelationCardFields> | null
  loading: boolean
}>()

const client = computed(() => useFragment(ClientRelationCardFields, props.client ?? null) ?? null)

const { date: clientSince } = useDate(computed(() => client.value?.createdAt))
// useDate renders "Invalid date" rather than nothing when the client has never been placed at.
const { date: firstPlacementDate } = useDate(computed(() => client.value?.firstHireStartedAt))
const firstPlacement = computed(() =>
  client.value?.firstHireStartedAt ? firstPlacementDate.value : null,
)

const supplierId = computed(() =>
  client.value && 'supplier' in client.value ? (client.value.supplier?.id ?? null) : null,
)

const scope = computed(() =>
  client.value ? placementScope(client.value) : { account: { id: '', name: '' }, endClient: undefined },
)

const { result: clientRelationUpcomingCounts } = useQuery(
  ClientRelationUpcomingCountsQuery,
  () =>
    scope.value.endClient
      ? {
          companies: [scope.value.account.id],
          endClients: [scope.value.endClient.id],
          range: soonRange.value,
        }
      : {
          supplierAccounts: [supplierId.value ?? ''],
          companies: [scope.value.account.id],
          range: soonRange.value,
        },
  () => ({
    clientId: 'publicApi',
    enabled: !!client.value && (!!scope.value.endClient || !!supplierId.value),
  }),
)

const endingSoonCount = computed(
  () => clientRelationUpcomingCounts.value?.endingSoon.paginatorInfo.total ?? 0,
)
const startingSoonCount = computed(
  () => clientRelationUpcomingCounts.value?.startingSoon.paginatorInfo.total ?? 0,
)

const placementsUrl = computed(() =>
  clientPlacementsUrl(scope.value.account, undefined, scope.value.endClient),
)
const activePlacementsUrl = computed(() =>
  clientPlacementsUrl(scope.value.account, [HireActiveStatus.Active], scope.value.endClient),
)
const endingSoonUrl = computed(() =>
  endingSoonPlacementsUrl(scope.value.account, soonRange.value, scope.value.endClient),
)
const startingSoonUrl = computed(() =>
  startingSoonPlacementsUrl(scope.value.account, soonRange.value, scope.value.endClient),
)
</script>

<style scoped lang="scss">
.client-tiles {
  @apply tw-w-full;
}

.client-tile {
  @apply tw-flex tw-grow tw-basis-0 tw-flex-col tw-justify-between tw-gap-sm tw-p-4;
  @apply tw-rounded tw-bg-true-gray-100;
}
</style>
